import { availabilityLabel } from '@shared/contracts/receivable-availability'
import { payoutStatusLabels } from '@entities/wallet/model/payout-display'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { ANALYTICS_TIME_ZONE } from '@shared/lib/analytics-period'
import { useQuery } from '@tanstack/react-query'
import { saleDetailQuery } from '@entities/sale'
import type { SaleDetail } from '@entities/sale'
import {
  KeyValueGrid,
  NavigationLink,
  DetailHero,
  Drawer,
  DrawerSection,
  MoneyValue,
  MoneyReconciliation,
  StatusBadge,
  TechnicalDetails,
} from '@shared/design-system/ui'
import {
  detailMoney,
  detailString,
  formatStatementDate,
  ledgerKindLabel,
  statementAmountVisible,
  statementIsDebt,
  statementEntryForDisplay,
  statementEventTone,
  statementEventTypeLabel,
  statementSaleIds,
  statementSaleReferenceLabel,
  statementSignPrefix,
  statementTitle,
  statementVisualDirection,
  statementVisualTone,
} from '@entities/wallet'
import type { StatementEntry, StatementVisualDirection } from '@entities/wallet'
import {
  creditAmount,
  debitAmount,
  ledgerBreakdown,
  ledgerBreakdownAmount,
  ledgerBreakdownLabel,
  ledgerBreakdownMeta,
  ledgerBreakdownRow,
  ledgerDetailBlock,
  ledgerDetail,
  ledgerDetailLabel,
  ledgerDetailRow,
  ledgerDetailRows,
  ledgerDetailSectionTitle,
  ledgerDetailValue,
  receivableSummary,
  receivableSummaryItem,
  receivableSummaryLabel,
  receivableSummaryMeta,
  receivableSummaryValue,
  receivableSummaryValueHeld,
  receivableSummaryValueIn,
  statementItem,
  statementItemHeader,
  statementItemSubtitle,
  statementItemTitle,
  statementMeta,
} from './wallet.css'

type LedgerEntryDrawerProps = {
  entry: StatementEntry | null
  onClose: () => void
  onViewSale: (saleId: string) => void
}

type MoneyLike = {
  amount: string
  currency: string
}

type DetailItemType = 'USER' | 'RESERVE' | 'COMMISSION' | 'VENDEPAY'

type DetailItem = {
  amount: unknown
  availableAt?: string
  availability?: unknown
  kind?: string
  ledgerEntryId?: string
  lineId?: string
  type?: DetailItemType
}

type DetailItemGroup = {
  key: string
  label: string
  lineId?: string
  items: DetailItem[]
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: ANALYTICS_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function amountClassName(direction: StatementVisualDirection) {
  if (direction === 'in') {
    return creditAmount
  }
  if (direction === 'out') {
    return debitAmount
  }
  return undefined
}

function moneyLike(value: unknown): MoneyLike | null {
  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { amount?: unknown }).amount === 'string' &&
    typeof (value as { currency?: unknown }).currency === 'string'
  ) {
    return value as MoneyLike
  }
  return null
}

function recordList(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> => typeof item === 'object' && item !== null,
      )
    : []
}

function balanceRouteLabel(entry: StatementEntry): string {
  return `Saldo em ${entry.currency}`
}

function detailKey(value: unknown, fallback: number): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : String(fallback)
}

function DetailMoney({ value }: { value: unknown }) {
  const money = moneyLike(value)
  return money ? <MoneyValue amount={money.amount} currency={money.currency} size="sm" /> : null
}

function DetailMoneyStrong({ value, tone }: { value: unknown; tone?: 'in' | 'held' }) {
  const money = moneyLike(value)
  if (!money) {
    return null
  }
  const toneClass =
    tone === 'in'
      ? receivableSummaryValueIn
      : tone === 'held'
        ? receivableSummaryValueHeld
        : undefined
  return (
    <MoneyValue amount={money.amount} currency={money.currency} size="md" className={toneClass} />
  )
}

function DetailSummaryRows({ entry }: { entry: StatementEntry }) {
  const referralProgram = useFeatureFlag('referralProgram')
  const receivingFeedback = referralProgram && entry.detail.role === 'referral'
  const userAmount = detailMoney(entry, 'userAmount')
  const reserveAmount = detailMoney(entry, 'reserveAmount')
  const remainingAmount = detailMoney(entry, 'remainingAmount')
  const sourceAmount = detailMoney(entry, 'sourceAmount')
  const availableAt = detailString(entry, 'availableAt')
  const reserveAvailableAt = firstDetailItemByType(entry, 'RESERVE')?.availableAt

  if (userAmount || reserveAmount) {
    return (
      <div className={ledgerDetailBlock}>
        <span className={ledgerDetailSectionTitle}>Composição no momento da venda</span>
        <div className={receivableSummary}>
          {userAmount ? (
            <div className={receivableSummaryItem}>
              <span className={receivableSummaryLabel}>Recebível registrado</span>
              <span className={receivableSummaryValue}>
                <DetailMoneyStrong value={userAmount} tone="in" />
              </span>
              <span className={receivableSummaryMeta}>
                {availabilityLabel(
                  entry.detail.availability,
                  availableAt ? formatStatementDate(availableAt) : undefined,
                )}
              </span>
            </div>
          ) : null}
          {reserveAmount ? (
            <div className={receivableSummaryItem}>
              <span className={receivableSummaryLabel}>Reserva registrada</span>
              <span className={receivableSummaryValue}>
                <DetailMoneyStrong value={reserveAmount} tone="held" />
              </span>
              <span className={receivableSummaryMeta}>
                {reserveAvailableAt
                  ? `Reserva prevista para ${formatStatementDate(reserveAvailableAt)}`
                  : receivingFeedback
                    ? availabilityLabel(firstDetailItemByType(entry, 'RESERVE')?.availability)
                    : 'Reserva temporária da venda'}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  const rows = [
    { label: 'Pendente nesta data', value: remainingAmount },
    { label: 'Devolvido ao saldo', value: detailMoney(entry, 'restoredAmount') },
    {
      label: 'Dívida pendente cancelada (sem entrada de dinheiro)',
      value: detailMoney(entry, 'outstandingAmount'),
    },
    { label: 'Valor solicitado', value: sourceAmount },
  ].filter((row) => row.value !== undefined)

  return (
    <>
      {rows.map((row) => (
        <div className={ledgerDetailRow} key={row.label}>
          <span className={ledgerDetailLabel}>{row.label}</span>
          <span className={ledgerDetailValue}>
            <DetailMoney value={row.value} />
          </span>
        </div>
      ))}
    </>
  )
}

function firstDetailItemByType(
  entry: StatementEntry,
  type: DetailItemType,
): DetailItem | undefined {
  return detailItems(entry).find((item) => item.type === type)
}

function detailItems(entry: StatementEntry): DetailItem[] {
  return recordList(entry.detail.items).map((item) => ({
    amount: item.amount,
    availableAt: typeof item.availableAt === 'string' ? item.availableAt : undefined,
    availability: item.availability,
    kind: typeof item.kind === 'string' ? item.kind : undefined,
    ledgerEntryId: typeof item.ledgerEntryId === 'string' ? item.ledgerEntryId : undefined,
    lineId: typeof item.lineId === 'string' ? item.lineId : undefined,
    type: detailItemType(item.type),
  }))
}

function detailItemType(value: unknown): DetailItemType | undefined {
  return value === 'USER' || value === 'RESERVE' || value === 'COMMISSION' || value === 'VENDEPAY'
    ? value
    : undefined
}

function detailItemGroups(items: DetailItem[]): DetailItemGroup[] {
  const groups = new Map<string, DetailItemGroup>()
  for (const [index, item] of items.entries()) {
    const key = item.lineId ?? item.ledgerEntryId ?? `${item.kind ?? 'item'}:${index}`
    const group = groups.get(key)
    if (group) {
      group.items.push(item)
      continue
    }
    groups.set(key, {
      key,
      label: ledgerKindLabel(item.kind) ?? 'Item da venda',
      lineId: item.lineId,
      items: [item],
    })
  }
  return Array.from(groups.values())
}

function itemBreakdownLabel(item: DetailItem): string {
  if (item.type === 'USER') {
    return 'Entra para você'
  }
  if (item.type === 'RESERVE') {
    return 'Reserva do item'
  }
  if (item.type === 'COMMISSION') {
    return 'Comissão'
  }
  if (item.type === 'VENDEPAY') {
    return 'Taxa da plataforma'
  }
  return 'Valor do item'
}

function itemBreakdownMeta(item: DetailItem, receivingFeedback: boolean): string | null {
  if (item.type === 'USER' || item.type === 'COMMISSION') {
    return availabilityLabel(
      item.availability,
      item.availableAt ? formatStatementDate(item.availableAt) : undefined,
    )
  }
  return item.availableAt
    ? `Libera em ${formatStatementDate(item.availableAt)}`
    : receivingFeedback && item.type === 'RESERVE'
      ? availabilityLabel(item.availability)
      : null
}

function DetailItems({ entry, sale }: { entry: StatementEntry; sale?: SaleDetail }) {
  const referralProgram = useFeatureFlag('referralProgram')
  const receivingFeedback = referralProgram && entry.detail.role === 'referral'
  const items = detailItems(entry)
  if (items.length === 0) {
    return null
  }
  const groups = detailItemGroups(items)
  const saleDetail = sale?.id === entry.saleId ? sale : undefined
  const subscriptionChargeWithoutLines =
    saleDetail?.lines?.length === 0 &&
    (saleDetail.isRenewal === true ||
      (saleDetail.isRenewal === false &&
        Boolean(saleDetail.subscriptionId) &&
        (saleDetail.subscriptionCycleN === 1 ||
          saleDetail.id === `trial:${saleDetail.subscriptionId}`)))

  return (
    <>
      {groups.length > 0 ? (
        <div className={ledgerDetailBlock}>
          <span className={ledgerDetailSectionTitle}>Composição do recebível</span>
          <div className={ledgerBreakdown}>
            {groups.map((group) => {
              const productName = group.lineId
                ? saleDetail?.lines?.find((line) => line.lineId === group.lineId)?.productName
                : subscriptionChargeWithoutLines
                  ? saleDetail.productName
                  : undefined
              return (
                <div className={statementItem} key={group.key}>
                  <div className={statementItemHeader}>
                    <span className={statementItemTitle}>{group.label}</span>
                    {group.lineId ? (
                      <span className={statementItemSubtitle}>{group.lineId}</span>
                    ) : null}
                  </div>
                  {productName ? <span className={statementMeta}>{productName}</span> : null}
                  {group.items.map((item, index) => (
                    <div
                      className={ledgerBreakdownRow}
                      key={`item-${detailKey(item.ledgerEntryId, index)}`}
                    >
                      <span className={ledgerBreakdownLabel}>
                        {itemBreakdownLabel(item)}
                        {itemBreakdownMeta(item, receivingFeedback) ? (
                          <span className={ledgerBreakdownMeta}>
                            {itemBreakdownMeta(item, receivingFeedback)}
                          </span>
                        ) : null}
                      </span>
                      <span className={ledgerBreakdownAmount}>
                        <DetailMoney value={item.amount} />
                      </span>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
          {(['userAmount', 'reserveAmount'] as const).map((key) => {
            const total = detailMoney(entry, key)
            const types = key === 'userAmount' ? ['USER', 'COMMISSION'] : ['RESERVE']
            const parts = items
              .filter((item) => item.type && types.includes(item.type))
              .map((item) => moneyLike(item.amount))
              .filter((value): value is MoneyLike => value !== null)
            return total && parts.length > 0 ? (
              <MoneyReconciliation
                key={key}
                total={total}
                parts={parts}
                label={
                  key === 'userAmount'
                    ? 'Arredondamento dos recebíveis'
                    : 'Arredondamento das reservas'
                }
              />
            ) : null
          })}
        </div>
      ) : null}
    </>
  )
}

function FxLossDetails({ entry }: { entry: StatementEntry }) {
  const fx = entry.detail.fx
  if (!fx || typeof fx !== 'object' || Array.isArray(fx)) return null
  const data = fx as Record<string, unknown>
  const original = moneyLike(data.originalBasis)
  const debit = moneyLike(data.providerDebit)
  const loss = moneyLike(data.loss)
  if (!original || !debit || !loss) return null
  return (
    <DrawerSection
      title="Perda cambial"
      description="A parcela deste lançamento segue sua participação na venda."
    >
      <KeyValueGrid
        items={[
          { label: 'Base original em reais', value: <MoneyValue {...original} size="sm" /> },
          { label: 'Débito efetivo na devolução', value: <MoneyValue {...debit} size="sm" /> },
          { label: 'Perda total da cobrança', value: <MoneyValue {...loss} size="sm" /> },
          { label: 'Parcela deste lançamento', value: <MoneyValue {...entry.amount} size="sm" /> },
        ]}
      />
    </DrawerSection>
  )
}

function knownLabel(
  labels: Readonly<Record<string, string>>,
  value: string | undefined,
  fallback: string,
) {
  return value && Object.hasOwn(labels, value) ? labels[value]! : fallback
}

function LedgerEntryBody({
  entry,
  onViewSale,
  sale,
}: {
  entry: StatementEntry
  onViewSale: (saleId: string) => void
  sale?: SaleDetail
}) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const displayEntry = statementEntryForDisplay(entry)
  const direction = statementVisualDirection(displayEntry.direction, displayEntry.eventType)
  const toneDirection = statementVisualTone(direction, displayEntry.eventType)
  const sign = statementSignPrefix(direction)
  const saleIds =
    displayEntry.detail.role === 'referral'
      ? []
      : statementIsDebt(displayEntry)
        ? displayEntry.saleId
          ? [displayEntry.saleId]
          : []
        : statementSaleIds(displayEntry)

  return (
    <div className={ledgerDetail} data-testid="wallet-ledger-entry-drawer">
      <DetailHero
        eyebrow={statementEventTypeLabel(displayEntry.eventType, receiptsExperience)}
        value={
          statementAmountVisible(displayEntry) ? (
            <span className={amountClassName(toneDirection)}>
              {sign ? `${sign} ` : ''}
              <MoneyValue
                amount={displayEntry.amount.amount}
                currency={displayEntry.amount.currency}
                size="xl"
              />
            </span>
          ) : (
            'Evento informativo'
          )
        }
        description={
          entry.detail.liabilityCancellation === true
            ? 'Dívida cancelada. Este lançamento não confirma devolução do valor cobrado.'
            : statementTitle(displayEntry, receiptsExperience)
        }
        aside={formatDateTime(displayEntry.occurredAt)}
      />

      <div className={ledgerDetailRows}>
        <div className={ledgerDetailRow}>
          <span className={ledgerDetailLabel}>Evento</span>
          <span className={ledgerDetailValue}>
            {statementEventTypeLabel(displayEntry.eventType, receiptsExperience)}
          </span>
        </div>
        <div className={ledgerDetailRow}>
          <span className={ledgerDetailLabel}>Data</span>
          <span className={ledgerDetailValue}>{formatDateTime(displayEntry.occurredAt)}</span>
        </div>
        {saleIds.map((saleId, index) => (
          <div className={ledgerDetailRow} key={saleId}>
            <span className={ledgerDetailLabel}>
              {statementIsDebt(displayEntry)
                ? 'Venda de origem'
                : index === 0
                  ? 'Venda'
                  : 'Venda relacionada'}
            </span>
            <NavigationLink onClick={() => onViewSale(saleId)}>
              {statementSaleReferenceLabel(saleId)}{' '}
            </NavigationLink>
          </div>
        ))}
        {statementIsDebt(displayEntry) ? (
          <div className={ledgerDetailRow}>
            <span className={ledgerDetailLabel}>Motivo</span>
            <span className={ledgerDetailValue}>
              {statementTitle(displayEntry, receiptsExperience)}
            </span>
          </div>
        ) : (
          <>
            <DetailSummaryRows entry={displayEntry} />
            <DetailItems entry={displayEntry} sale={sale} />
          </>
        )}
        <div className={ledgerDetailRow}>
          <span className={ledgerDetailLabel}>Carteira</span>
          <span className={ledgerDetailValue}>{balanceRouteLabel(displayEntry)}</span>
        </div>
      </div>
      <FxLossDetails entry={entry} />
      {entry.eventType === 'WITHDRAWAL_OBSERVED' ? (
        <DrawerSection
          title={receiptsExperience ? 'Saque automático' : 'Repasse observado na Stripe'}
          description={
            receiptsExperience
              ? 'Evento informativo. A atualização do saque não altera o saldo da carteira e não confirma depósito bancário.'
              : 'Evento informativo. A observação na Stripe não aplica um efeito financeiro na carteira e não confirma depósito bancário.'
          }
        >
          <KeyValueGrid
            items={[
              {
                label: 'Valor observado',
                value: entry.amount ? <MoneyValue {...entry.amount} size="sm" /> : 'Não informado',
              },
              {
                label: receiptsExperience ? 'Status do saque' : 'Status na Stripe',
                value: knownLabel(
                  payoutStatusLabels(receiptsExperience),
                  detailString(entry, 'status'),
                  'Não informado',
                ),
              },
              {
                label: 'Conciliação na carteira neste evento',
                value: knownLabel(
                  {
                    pending: 'Pendente, sem efeito financeiro deste repasse',
                    completed: 'Efeito aplicado',
                    reversed: 'Efeito revertido',
                  },
                  detailString(entry, 'ledgerStatus'),
                  'Não informada',
                ),
              },
            ]}
          />
          {receiptsExperience && recordList(entry.detail.observations).length > 0 ? (
            <div data-testid="payout-observation-history">
              <h3>Histórico de atualizações</h3>
              {recordList(entry.detail.observations).map((observation) => (
                <p key={String(observation.id)}>
                  {typeof observation.occurredAt === 'string'
                    ? formatDateTime(observation.occurredAt)
                    : 'Data não informada'}
                  :{' '}
                  {knownLabel(
                    payoutStatusLabels(receiptsExperience),
                    typeof observation.status === 'string' ? observation.status : undefined,
                    'Status não informado',
                  )}
                </p>
              ))}
            </div>
          ) : null}
        </DrawerSection>
      ) : null}
      <DrawerSection
        title="Rastreabilidade"
        description="Referências para suporte e conciliação, sem alterar o saldo exibido."
      >
        <TechnicalDetails
          rows={[
            { label: 'Lançamento', value: entry.id },
            ...saleIds.map((saleId, index) => ({
              label: index === 0 ? 'Venda' : `Venda relacionada ${index}`,
              value: saleId,
            })),
          ]}
        />
      </DrawerSection>
    </div>
  )
}

export function LedgerEntryDrawer({ entry, onClose, onViewSale }: LedgerEntryDrawerProps) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const loadProductNames =
    entry !== null &&
    entry.eventType === 'SALE_SETTLED' &&
    entry.detail.role !== 'referral' &&
    Boolean(entry.saleId) &&
    detailItems(entry).length > 0
  const sale = useQuery({
    ...saleDetailQuery(entry?.saleId ?? '', 'charge'),
    enabled: loadProductNames,
    refetchInterval: false,
  })
  return (
    <Drawer
      open={entry !== null}
      onClose={onClose}
      title={entry ? statementTitle(entry, receiptsExperience) : 'Detalhe do evento'}
      description={
        entry ? formatDateTime(entry.occurredAt) : 'Origem, composição e efeito desta movimentação.'
      }
      size="wide"
      variant="financial"
      status={
        entry ? (
          <StatusBadge
            tone={statementEventTone(entry.eventType)}
            label={statementEventTypeLabel(entry.eventType, receiptsExperience)}
          />
        ) : undefined
      }
    >
      {entry ? (
        <LedgerEntryBody
          entry={entry}
          onViewSale={onViewSale}
          sale={loadProductNames && !sale.isError ? sale.data : undefined}
        />
      ) : null}
    </Drawer>
  )
}
